import { useMemo, useState } from 'react';
import { useQuery } from 'react-query';
import { Divider, Box, useMediaQuery } from '@mui/material';
import {
  IPandaDocContractActivity,
  IResponse,
  PandaDocContractSort,
  IDateRange,
  PandaDocDocumentStatus,
  PandaDocActivityType
} from '../../../models';
import { getContractActivity } from '../../../fetch';
import {
  Page,
  PageTitle,
  Table,
  Pagination,
  SearchSection,
  MobileTable
} from '../../../components';
import { ContractFilters } from './filters';
import { formatDate, unCamelCase } from '../../../helpers';

export const VendorContractsActivityPage = () => {
  const [page, setPage] = useState<number>(0);
  const [recordCount, setRecordCount] = useState<number>(0);
  const [hasAppliedFilters, setHasAppliedFilters] = useState<boolean>(false);
  const [perPage, setRowsPerPage] = useState<number>(10);
  const [filters, setFilters] = useState<{
    searchTerm?: string;
    startCreatedDate?: string;
    endCreatedDate?: string;
    documentStatus?: string | number;
    activityType?: string | number;
  } | null>({});
  const [searchedValue, setSearchedValue] = useState<string>('');
  const [searchValue, setSearchValue] = useState<string>('');
  const [selectedSort, setSelectedSort] = useState<string>('CreatedDate');
  const [sortDirection, setSortDirection] = useState<{
    DocumentName?: 'Asc' | 'Desc';
    DocumentStatus?: 'Asc' | 'Desc';
    VendorName?: 'Asc' | 'Desc';
    ActivityType?: 'Asc' | 'Desc';
    ActivityDescription?: 'Asc' | 'Desc';
    CreatedDate?: 'Asc' | 'Desc';
  }>({
    CreatedDate: 'Asc'
  });
  const [selectedDateRange, setSelectedDateRange] = useState<IDateRange>({
    startDate: null,
    endDate: null,
    key: 'selection',
    inputValue: ''
  });
  const [selectedDocumentStatus, setSelectedDocumentStatus] = useState<string | number>('');
  const [selectedActivityType, setSelectedActivityType] = useState<string | number>('');

  const isMobile = useMediaQuery('(max-width: 960px)');

  // react-query
  const { isLoading, data: vendorContractsActivityData } = useQuery<
    IResponse<IPandaDocContractActivity[]>,
    Error
  >(
    [
      'getVendorContractActivity',
      page,
      perPage,
      sortDirection,
      selectedSort,
      searchedValue,
      filters
    ],
    () =>
      getContractActivity({
        page: page + 1,
        perPage,
        sortBy: selectedSort as PandaDocContractSort,
        sortDirection: sortDirection[selectedSort] as string,
        searchTerm: searchedValue || filters?.searchTerm,
        startCreatedDate: filters?.startCreatedDate,
        endCreatedDate: filters?.endCreatedDate,
        documentStatus: filters?.documentStatus as PandaDocDocumentStatus,
        activityType: filters?.activityType as PandaDocActivityType
      }),
    {
      onSuccess: data => {
        setRecordCount(data.totalRecordCount);
      },
      notifyOnChangeProps: 'tracked'
    }
  );
  const vendorContractsActivity = useMemo(
    () => vendorContractsActivityData?.records || [],
    [vendorContractsActivityData]
  );

  const handleClickColumn = (column: string) => {
    setSelectedSort(column);
    setSortDirection({
      ...sortDirection,
      [column]: sortDirection[column] === 'Asc' ? 'Desc' : 'Asc'
    });
  };
  const columns = useMemo(() => {
    return [
      {
        Header: 'Created',
        accessor: 'createdDate',
        isServerSorted: selectedSort === 'CreatedDate',
        isServerSortedDesc: sortDirection.CreatedDate === 'Desc',
        handleClickColumn: () => handleClickColumn('CreatedDate'),
        Cell: props => <>{formatDate(props.cell.row.original.createdDate)}</>
      },
      {
        Header: 'Document Name',
        accessor: 'pandaDocDocumentName',
        isServerSorted: selectedSort === 'DocumentName',
        isServerSortedDesc: sortDirection.DocumentName === 'Desc',
        handleClickColumn: () => handleClickColumn('DocumentName')
      },
      {
        Header: 'Vendor Name',
        accessor: 'vendorName',
        isServerSorted: selectedSort === 'VendorName',
        isServerSortedDesc: sortDirection.VendorName === 'Desc',
        handleClickColumn: () => handleClickColumn('VendorName')
      },
      {
        Header: 'Status',
        accessor: 'documentStatus',
        isServerSorted: selectedSort === 'DocumentStatus',
        isServerSortedDesc: sortDirection.DocumentStatus === 'Desc',
        handleClickColumn: () => handleClickColumn('DocumentStatus')
      },
      {
        Header: 'Activity Type',
        accessor: 'activityType',
        isServerSorted: selectedSort === 'ActivityType',
        isServerSortedDesc: sortDirection.ActivityType === 'Desc',
        handleClickColumn: () => handleClickColumn('ActivityType'),
        Cell: props => <>{unCamelCase(props.cell.row.original.activityType)}</>
      },
      {
        Header: 'Activity',
        accessor: 'activityDescription',
        isServerSorted: selectedSort === 'ActivityDescription',
        isServerSortedDesc: sortDirection.ActivityDescription === 'Desc',
        handleClickColumn: () => handleClickColumn('ActivityDescription')
      }
    ];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [vendorContractsActivity, selectedSort, sortDirection]);

  return (
    <Page title="Vendor Contract Activity">
      <PageTitle title="Vendor Contract Activity" showDivider={false} />
      <Divider />
      <Box mt={1}>
        <ContractFilters
          isLoading={isLoading}
          setHasAppliedFilters={setHasAppliedFilters}
          hasAppliedFilters={hasAppliedFilters}
          setSearchValue={setSearchValue}
          setSearchedValue={setSearchedValue}
          selectedDateRange={selectedDateRange}
          setSelectedDateRange={setSelectedDateRange}
          searchValue={searchValue}
          setSelectedDocumentStatus={setSelectedDocumentStatus}
          selectedDocumentStatus={selectedDocumentStatus}
          setSelectedActivityType={setSelectedActivityType}
          selectedActivityType={selectedActivityType}
          handleSearch={(val: string) => {
            setPage(0);
            setSearchedValue(val);
          }}
          applyFilters={(clearFilters?: boolean, clearSearch?: boolean) => {
            if (clearFilters) {
              setFilters({
                searchTerm: '',
                startCreatedDate: undefined,
                endCreatedDate: undefined,
                documentStatus: undefined,
                activityType: undefined
              });
            } else {
              setPage(0);
              setFilters({
                ...filters,
                searchTerm: searchValue,
                startCreatedDate: selectedDateRange?.startDate?.toISOString() || undefined,
                endCreatedDate: selectedDateRange?.endDate?.toISOString() || undefined,
                documentStatus: selectedDocumentStatus || undefined,
                activityType: selectedActivityType || undefined
              });
            }
          }}
        />
        {searchedValue && (
          <SearchSection
            searchedValue={searchedValue}
            handleClearSearch={() => {
              setSearchValue('');
              setSearchedValue('');
              setHasAppliedFilters(false);
              setPage(0);
            }}
          />
        )}
      </Box>
      <Table
        columns={columns}
        data={vendorContractsActivity}
        isLoading={isLoading}
        hidePagination
        noResultsText="No data found"
        ResponsiveComponent={isMobile ? MobileTable : undefined}
        mobileProps={{
          fields: columns
            .filter(c => c.Header)
            .map(c => ({
              name: c.Header,
              accessor: c.accessor,
              Cell: c?.Cell
            })),
          useFirstCell: true
        }}
      />
      {!isLoading && (
        <Pagination
          page={page}
          count={recordCount}
          rowsPerPage={perPage}
          setPage={setPage}
          setRowsPerPage={setRowsPerPage}
        />
      )}
    </Page>
  );
};
